import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useSelectedCity } from '../contexts/SelectedCityContext';
import { fetchAllPaginated } from '../services/api';
import { getPainelRede } from '../services/dashboardService';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import SearchableSelect from '../components/SearchableSelect';
import {
  KpiCard, MonthlyBars, RankedBars, NotasDistribution, SectionCard,
  HabilidadesDistribution, ConceitosRows, ConceitosLegenda,
  cardCls, fmt, pct, rateColor, mediaColor,
} from '../components/PainelDiarioUI';

/**
 * Painel da Rede — visão da coordenação sobre o diário. É a aba "Diário" do
 * Dashboard (o antigo endereço /diarios/painel-rede redireciona para ela).
 *
 * Agrega frequência, notas e aulas que os professores já lançam
 * (/dashboard-diario/rede/). Responde "como vai a rede?" sem abrir turma por
 * turma. O escopo segue o papel do usuário (o backend limita as turmas).
 *
 * O 1º e o 2º ano não lançam nota, e sim conceitos por habilidade (Registro
 * de Habilidades): eles aparecem numa seção própria. Com o filtro só nessas
 * séries, os cartões de nota (que ficariam vazios) dão lugar a ela.
 *
 * O cartão "Turmas com diário" leva à lista dessas turmas, cada uma com
 * atalho para o diário — o número sozinho não dizia onde procurar.
 *
 * A régua é uma só em toda a tela: média 7,0 aprova, 75% é o piso de
 * frequência. "Alunos em risco" é quem está abaixo de qualquer uma delas.
 */
function PainelRede() {
  const { darkMode } = useTheme();
  const { selectedCityId } = useSelectedCity();

  const currentYear = new Date().getFullYear();
  const [schoolYear] = useState(currentYear);
  const [escolas, setEscolas] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [idSchool, setIdSchool] = useState('');
  const [grade, setGrade] = useState('');
  const [shift, setShift] = useState('');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPanel, setLoadingPanel] = useState(false);
  const [error, setError] = useState(null);
  const turmasComDiarioRef = useRef(null);

  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';
  const strong = darkMode ? 'text-slate-100' : 'text-slate-800';

  // A cidade do menu lateral entra sozinha nas requisições (comCidadeAtiva);
  // trocá-la recarrega escolas, turmas e o painel, e solta a escola
  // escolhida, que era da outra cidade.
  useEffect(() => {
    let alive = true;
    setIdSchool('');
    (async () => {
      try {
        const [esc, tur] = await Promise.all([
          fetchAllPaginated('/schools/').catch(() => []),
          fetchAllPaginated('/classes/').catch(() => []),
        ]);
        if (!alive) return;
        setEscolas(esc);
        setTurmas(tur);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [selectedCityId]);

  const grades = useMemo(() => {
    const set = new Set(turmas.map(t => t.grade).filter(Boolean));
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b), 'pt-BR'));
  }, [turmas]);

  const shifts = useMemo(() => {
    const set = new Set(turmas.map(t => t.shift).filter(Boolean));
    return Array.from(set);
  }, [turmas]);

  const fetchPanel = useCallback(async () => {
    setLoadingPanel(true);
    setError(null);
    try {
      setData(await getPainelRede({ schoolYear, idSchool, grade, shift, city: selectedCityId }));
    } catch (err) {
      console.error('Erro ao carregar painel da rede:', err);
      setError('Não foi possível carregar o painel da rede.');
    } finally {
      setLoadingPanel(false);
    }
  }, [schoolYear, idSchool, grade, shift, selectedCityId]);

  useEffect(() => { fetchPanel(); }, [fetchPanel]);

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Painel da Rede..."
        subtitle="Buscando escolas e turmas..."
        size="xl"
      />
    );
  }

  const t = data?.totals;
  const freq = data?.frequencia;
  const notas = data?.notas;
  const hab = data?.habilidades;
  const rk = data?.rankings;
  const temHabilidades = Boolean(hab?.total);
  // Só esconde as notas quando o recorte não tem nenhuma e tem habilidades
  // (ex.: filtro "1º Ano"); sem nenhum dos dois, segue o painel de sempre.
  const mostraNotas = Boolean(notas?.total) || !temHabilidades;

  return (
    <>
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
            ? 'bg-gradient-to-r from-gray-50 to-gray-400'
            : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'}`}>
            Painel da Rede
          </h1>
          <p className={`text-sm mt-1 ${muted}`}>
            Frequência, notas, habilidades e aulas — a partir do que os professores lançam no diário · {schoolYear}
          </p>
        </div>
      </header>

      {/* Filtros em grade, e não em linha: o campo de escola cresce até o nome
          mais comprido da lista e, solto num flex, empurrava a página para
          fora da tela do celular. Uma coluna que pode encolher resolve em
          qualquer largura. */}
      <div className={`rounded-xl p-4 mb-6 ${cardCls(darkMode)}`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <div className="min-w-0 sm:col-span-2 lg:col-span-2">
            <label className={`block text-xs font-medium mb-1 ${muted}`}>Escola</label>
            <SearchableSelect
              value={idSchool}
              onChange={setIdSchool}
              options={escolas.map(e => ({ value: e.id, label: e.school }))}
              emptyOptionLabel="Todas"
              searchPlaceholder="Buscar escola..."
              darkMode={darkMode}
            />
          </div>
          <div className="min-w-0">
            <label className={`block text-xs font-medium mb-1 ${muted}`}>Ano / Série</label>
            <SearchableSelect
              value={grade}
              onChange={setGrade}
              options={grades}
              emptyOptionLabel="Todos"
              searchPlaceholder="Buscar ano/série..."
              darkMode={darkMode}
            />
          </div>
          <div className="min-w-0">
            <label className={`block text-xs font-medium mb-1 ${muted}`}>Turno</label>
            <SearchableSelect
              value={shift}
              onChange={setShift}
              options={shifts}
              emptyOptionLabel="Todos"
              darkMode={darkMode}
            />
          </div>
        </div>
        {(idSchool || grade || shift || loadingPanel) && (
          <div className="flex flex-wrap items-center gap-3 mt-3">
            {(idSchool || grade || shift) && (
              <button
                onClick={() => { setIdSchool(''); setGrade(''); setShift(''); }}
                className={`text-sm px-3 py-2 rounded-lg ${darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                Limpar filtros
              </button>
            )}
            {loadingPanel && <span className={`text-xs ${muted}`}>Atualizando…</span>}
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-xl p-4 mb-6 bg-[#E83A3C]/10 text-[#E83A3C] text-sm">{error}</div>
      )}

      {data && (
        <>
          {/* Totais da rede */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <KpiCard label="Escolas" value={fmt(t.schools)} tone="nav" darkMode={darkMode} />
            <KpiCard label="Turmas" value={fmt(t.classes)} tone="nav" darkMode={darkMode} />
            <KpiCard label="Alunos" value={fmt(t.students)} tone="accent" darkMode={darkMode} />
            <KpiCard label="Professores" value={fmt(t.teachers)} tone="accent" darkMode={darkMode} />
          </div>

          {/* O que os professores estão lançando */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KpiCard label="Aulas registradas" value={fmt(data.aulas.total)} foot="no ano" tone="accent" darkMode={darkMode} />
            <KpiCard label="Turmas com diário" value={pct(data.diario.adesao_pct)}
              foot={`${data.diario.turmas_ativas} de ${data.diario.turmas_total} · ver turmas ↓`}
              tone={data.diario.adesao_pct >= 70 ? 'good' : 'warn'} darkMode={darkMode}
              onClick={() => turmasComDiarioRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
            <KpiCard label="Frequência média" value={pct(freq.geral)}
              tone={freq.geral >= 90 ? 'good' : freq.geral >= 75 ? 'warn' : 'crit'} darkMode={darkMode} />
            {mostraNotas ? (
              <KpiCard label="Aprovação (≥ 7,0)" value={pct(notas.aprovado?.pct)}
                foot={notas.media_geral != null ? `média geral ${fmt(notas.media_geral)}` : 'sem notas'}
                tone={(notas.aprovado?.pct || 0) >= 70 ? 'good' : 'warn'} darkMode={darkMode} />
            ) : (
              <KpiCard label="Habilidades consolidadas" value={pct(hab.HC?.pct)}
                foot="HC · Infantil, 1º e 2º ano" tone="good" darkMode={darkMode} />
            )}
          </div>

          {/* O bloco acionável vem antes dos gráficos */}
          <div className="mb-4">
            <AlunosEmRisco risco={data.alunos_em_risco} darkMode={darkMode} />
          </div>

          {/* Gráficos */}
          {mostraNotas ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                <SectionCard title="Aulas registradas por mês" subtitle="ritmo de preenchimento do diário ao longo do ano" darkMode={darkMode}>
                  <MonthlyBars data={data.aulas.por_mes} darkMode={darkMode} />
                </SectionCard>
                <SectionCard title="Distribuição das notas" subtitle="médias anuais lançadas (por aluno × disciplina)" darkMode={darkMode}>
                  <NotasDistribution notas={notas} darkMode={darkMode} />
                </SectionCard>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                <SectionCard title="Média final por ano/série" subtitle="linha vermelha = aprovação (7,0)" darkMode={darkMode}>
                  <RankedBars mode="nota" suffix="" max={10}
                    rows={(notas.por_ano || []).map(a => ({ label: a.grade, value: a.media, sub: `${a.alunos} nota(s)` }))}
                    darkMode={darkMode} />
                </SectionCard>
                <SectionCard title="Média por disciplina" subtitle="da menor para a maior · linha vermelha = aprovação (7,0)" darkMode={darkMode}>
                  <RankedBars mode="nota" suffix="" max={10}
                    rows={(notas.por_disciplina || []).map(d => ({ label: d.subject_name, value: d.media, sub: `${d.notas} nota(s)` }))}
                    darkMode={darkMode} />
                </SectionCard>
              </div>

              {/* Largura toda: os nomes de escola são compridos. */}
              <SectionCard title="Frequência média por escola" subtitle="linha vermelha = piso de 75%" darkMode={darkMode} className="mb-4">
                <RankedBars mode="freq"
                  rows={(freq.por_escola || []).map(e => ({ label: e.school_name, value: e.rate }))}
                  darkMode={darkMode} />
              </SectionCard>
            </>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <SectionCard title="Aulas registradas por mês" subtitle="ritmo de preenchimento do diário ao longo do ano" darkMode={darkMode}>
                <MonthlyBars data={data.aulas.por_mes} darkMode={darkMode} />
              </SectionCard>
              <SectionCard title="Frequência média por escola" subtitle="linha vermelha = piso de 75%" darkMode={darkMode}>
                <RankedBars mode="freq"
                  rows={(freq.por_escola || []).map(e => ({ label: e.school_name, value: e.rate }))}
                  darkMode={darkMode} />
              </SectionCard>
            </div>
          )}

          {/* Registro de Habilidades — Educação Infantil, 1º e 2º ano, que não lançam nota */}
          {temHabilidades && (
            <section className="mt-6 mb-4">
              <h2 className={`text-base font-semibold ${strong}`}>Registro de Habilidades · Educação Infantil, 1º e 2º ano</h2>
              <p className={`text-xs mt-0.5 mb-3 ${muted}`}>
                Nessas séries o professor lança um conceito por habilidade em cada bimestre, no lugar da nota ·{' '}
                {hab.turmas_com_registro} de {hab.turmas} turma(s) com conceitos lançados
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                <SectionCard title="Conceitos na rede" subtitle="último conceito de cada aluno em cada habilidade" darkMode={darkMode}>
                  <HabilidadesDistribution habilidades={hab} darkMode={darkMode} />
                </SectionCard>
                <SectionCard title="Por ano/série" subtitle="onde a aprendizagem pede reforço" darkMode={darkMode}>
                  <ConceitosRows darkMode={darkMode}
                    rows={(hab.por_serie || []).map((x) => ({ label: x.serie, contagem: x }))} />
                  <ConceitosLegenda darkMode={darkMode} />
                </SectionCard>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <SectionCard title="Evolução por bimestre" subtitle="conceitos lançados em cada bimestre" darkMode={darkMode}>
                  <ConceitosRows darkMode={darkMode}
                    rows={(hab.por_bimestre || []).map((b) => ({ label: `${b.bimestre}º bimestre`, contagem: b }))} />
                </SectionCard>
                <SectionCard title="Por componente / campo de experiência" subtitle="último conceito de cada habilidade" darkMode={darkMode}>
                  <ConceitosRows darkMode={darkMode}
                    rows={(hab.por_componente || []).map((c) => ({ label: c.componente, contagem: c }))} />
                </SectionCard>
                <RankTable title="Turmas com mais habilidades consolidadas" subtitle="% em HC" darkMode={darkMode}
                  rows={hab.melhores_turmas || []} valueKey="hc_pct" valueFmt={(v) => pct(v)} />
              </div>
            </section>
          )}

          <div ref={turmasComDiarioRef} className="scroll-mt-4 mb-4">
            <TurmasComDiario turmas={data.diario.turmas || []} total={data.diario.turmas_total} darkMode={darkMode} />
          </div>

          {/* Rankings */}
          <div className={`grid grid-cols-1 gap-4 ${mostraNotas ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
            {mostraNotas && (
              <RankTable title="Turmas mais bem avaliadas" darkMode={darkMode}
                rows={rk.melhores_medias} valueKey="media" valueFmt={(v) => fmt(v)} />
            )}
            <RankTable title="Melhores frequências" darkMode={darkMode}
              rows={rk.melhores_frequencias} valueKey="rate" valueFmt={(v) => pct(v)} valueColor />
            <RankTable title="Diário em atraso" subtitle="menor adesão — priorizar acompanhamento" darkMode={darkMode}
              rows={rk.menor_adesao} labelKey="school_name"
              valueKey="adesao_pct" valueFmt={(v) => pct(v)} />
          </div>

          <p className={`mt-8 pt-5 border-t text-sm ${darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-500'}`}>
            Todos os números vêm do <b className={strong}>Diário de Turma</b> — frequência, aulas, notas e habilidades que os
            professores já lançam. Nenhuma tabela nova. O que você vê está limitado ao seu escopo de acesso.
          </p>
        </>
      )}
    </>
  );
}

function formatBr(isoDate) {
  if (!isoDate) return '—';
  const [y, m, d] = String(isoDate).split('-');
  return `${d}/${m}/${y}`;
}

const linhaCls = (darkMode) => (darkMode ? 'border-t border-slate-700/60' : 'border-t border-slate-100');
const theadCls = (darkMode) => `sticky top-0 text-xs ${darkMode ? 'bg-slate-800 text-slate-400' : 'bg-white text-slate-500'}`;

// Do menor para o maior, com quem não tem o dado no fim da lista.
const ascSemNulos = (campo) => (a, b) =>
  (a[campo] == null) - (b[campo] == null) || (a[campo] ?? 0) - (b[campo] ?? 0);

const ORDENS = {
  aulas: { rotulo: 'Mais aulas', fn: null },  // já vem assim do backend
  media: { rotulo: 'Menor média', fn: ascSemNulos('media') },
  frequencia: { rotulo: 'Menor frequência', fn: ascSemNulos('frequencia') },
};

/**
 * Turmas com ao menos uma aula registrada no ano, com média e frequência,
 * busca por turma ou escola e atalho para o diário. Ordenar pela menor média
 * ou frequência mostra onde a turma pede reforço.
 */
function TurmasComDiario({ turmas, total, darkMode }) {
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState('aulas');
  const visiveis = useMemo(() => {
    // Sem acento e sem caixa: "joao" acha "JOÃO".
    const norm = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const q = norm(busca.trim());
    const filtradas = q
      ? turmas.filter(t => norm(`${t.class_name || ''} ${t.school_name || ''} ${t.grade || ''}`).includes(q))
      : turmas;
    const fn = ORDENS[ordem].fn;
    return fn ? [...filtradas].sort(fn) : filtradas;
  }, [turmas, busca, ordem]);

  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';
  const campoCls = `px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${
    darkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'}`;
  return (
    <SectionCard
      title="Turmas com diário preenchido"
      subtitle={`${turmas.length} de ${total} turma(s) com aula registrada no ano · clique na turma para abrir o diário`}
      darkMode={darkMode}
    >
      {turmas.length === 0 ? (
        <div className={`text-sm py-4 text-center ${muted}`}>Nenhuma turma com aula registrada neste recorte.</div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <input
              type="search"
              value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder="Buscar turma ou escola..."
              className={`flex-1 min-w-0 ${campoCls}`}
            />
            <select value={ordem} onChange={e => setOrdem(e.target.value)} aria-label="Ordenar turmas" className={campoCls}>
              {Object.entries(ORDENS).map(([valor, o]) => <option key={valor} value={valor}>{o.rotulo}</option>)}
            </select>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className={theadCls(darkMode)}>
                <tr>
                  <th className="py-2 pr-2 text-left font-medium">Turma</th>
                  <th className="py-2 px-2 text-right font-medium">Aulas</th>
                  <th className="py-2 px-2 text-right font-medium">Média</th>
                  <th className="py-2 px-2 text-right font-medium">Freq.</th>
                  <th className="py-2 pl-2 text-right font-medium hidden md:table-cell">Última aula</th>
                  <th className="py-2 pl-2 hidden sm:table-cell" />
                </tr>
              </thead>
              <tbody>
                {visiveis.map(t => (
                  <tr key={t.id_class} className={linhaCls(darkMode)}>
                    <td className="py-2 pr-2 min-w-0">
                      <Link to={`/turmas/${t.id_class}/diario`}
                        className={`hover:underline ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {t.class_name || '—'}
                      </Link>
                      <div className={`text-[11px] ${muted}`}>
                        {[t.school_name, t.grade, t.shift].filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td className={`py-2 px-2 text-right font-semibold tabular-nums ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{t.aulas}</td>
                    <td className={`py-2 px-2 text-right font-semibold tabular-nums ${mediaColor(t.media)}`}>{t.media != null ? fmt(t.media) : '—'}</td>
                    <td className={`py-2 px-2 text-right font-semibold tabular-nums ${rateColor(t.frequencia)}`}>{pct(t.frequencia)}</td>
                    <td className={`py-2 pl-2 text-right tabular-nums hidden md:table-cell ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{formatBr(t.ultima_aula)}</td>
                    <td className="py-2 pl-2 text-right whitespace-nowrap hidden sm:table-cell">
                      <Link to={`/turmas/${t.id_class}/diario`} className="text-[#0974BA] hover:underline text-xs font-medium">
                        Abrir diário →
                      </Link>
                    </td>
                  </tr>
                ))}
                {visiveis.length === 0 && (
                  <tr><td colSpan={6} className={`text-sm py-4 text-center ${muted}`}>Nenhuma turma encontrada.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </SectionCard>
  );
}

/**
 * Alunos abaixo da média de aprovação (7,0) ou do piso de frequência (75%),
 * da menor média para a maior. O backend manda os 50 primeiros e o total.
 */
function AlunosEmRisco({ risco, darkMode }) {
  const alunos = risco?.alunos || [];
  const total = risco?.total || 0;
  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';
  return (
    <SectionCard
      title={`Alunos em risco${total ? ` · ${fmt(total)}` : ''}`}
      subtitle={`média abaixo de 7,0 ou frequência abaixo de 75%${total > alunos.length ? ` · mostrando os ${alunos.length} de menor média` : ''}`}
      darkMode={darkMode}
    >
      {alunos.length === 0 ? (
        <div className={`text-sm py-4 text-center ${muted}`}>Nenhum aluno abaixo da média ou do mínimo de frequência.</div>
      ) : (
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className={theadCls(darkMode)}>
              <tr>
                <th className="py-2 pr-2 text-left font-medium">Aluno</th>
                <th className="py-2 px-2 text-right font-medium">Média</th>
                <th className="py-2 px-2 text-right font-medium">Freq.</th>
                <th className="py-2 pl-2 text-left font-medium hidden md:table-cell">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {alunos.map(a => (
                <tr key={a.id_student} className={linhaCls(darkMode)}>
                  <td className="py-2 pr-2 min-w-0">
                    <div className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{a.student_name}</div>
                    <div className={`text-[11px] ${muted}`}>
                      <Link to={`/turmas/${a.id_class}/diario`} className="hover:underline">{a.class_name || '—'}</Link>
                      {a.school_name ? ` · ${a.school_name}` : ''}
                    </div>
                  </td>
                  <td className={`py-2 px-2 text-right font-semibold tabular-nums ${mediaColor(a.media)}`}>{a.media != null ? fmt(a.media) : '—'}</td>
                  <td className={`py-2 px-2 text-right font-semibold tabular-nums ${rateColor(a.frequencia)}`}>{pct(a.frequencia)}</td>
                  <td className="py-2 pl-2 hidden md:table-cell">
                    <div className="flex flex-wrap gap-1.5">
                      {a.motivos.map(m => (
                        <span key={m} className="px-2 py-0.5 rounded text-[11px] font-medium text-[#E83A3C] bg-[#E83A3C]/10">{m}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

/** Tabela de ranking compacta (turmas/escolas). */
function RankTable({ title, subtitle, rows = [], labelKey = 'class_name', valueKey, valueFmt, valueColor, darkMode }) {
  return (
    <SectionCard title={title} subtitle={subtitle} darkMode={darkMode}>
      {rows.length === 0 ? (
        <div className={`text-sm py-4 text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Sem dados.</div>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={i > 0 ? (darkMode ? 'border-t border-slate-700/60' : 'border-t border-slate-100') : ''}>
                <td className={`py-2 pr-2 tabular-nums ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} style={{ width: 20 }}>{i + 1}</td>
                <td className="py-2">
                  <div className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{r[labelKey] || '—'}</div>
                  {r.school_name && labelKey !== 'school_name' && (
                    <div className={`text-[11px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{r.school_name}</div>
                  )}
                  {r.turmas_total !== undefined && (
                    <div className={`text-[11px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{r.turmas_ativas} de {r.turmas_total} turmas</div>
                  )}
                </td>
                <td className={`py-2 text-right font-semibold tabular-nums ${valueColor ? rateColor(r[valueKey]) : (darkMode ? 'text-slate-100' : 'text-slate-800')}`}>
                  {valueFmt(r[valueKey])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SectionCard>
  );
}

export default PainelRede;
